import { FetchBusinessesRequest } from '@containers/businesses/businesses.actions'
import { mapRoutes } from '../../containers/map/map.routing'
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core'
import { FetchPropertiesRequest } from '@containers/properties/properties.actions'
import { getPropertiesState } from '@containers/properties/properties.selectors'
import {
  FetchTemplateTagsRequest,
  FetchTestTemplateRequest,
} from '@containers/templates/templates.actions'
import {
  getTemplateTagsState,
  getTestTemplateState,
} from '@containers/templates/templates.selectors'
import { select, Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { FetchPropertyTypesByCodeRequest } from '@shared/shared.actions'
import { getPropertyTypesByCodeState } from '@shared/shared.select'
import { debounceTime, Subject, Subscription } from 'rxjs'
import { getBusinessesState } from '@containers/businesses/businesses.selectors'
import { getCompoundsState } from '@containers/compounds/compounds.selectors'
import { FetchCompoundsRequest } from '@containers/compounds/compounds.actions'

@Component({
  selector: 'test-template',
  templateUrl: './test-template.component.html',
  styleUrls: ['./test-template.component.css'],
})
export class TestTemplateComponent implements OnInit, OnDestroy {
  @Input() public open: boolean
  @Output() public onClose: EventEmitter<any> = new EventEmitter()

  public templates = []
  public propertiesOptions = []
  public businessesOptions = []
  public compoundsOptions = []
  public propertyTypeOptions = []
  public selectedTemplate: string
  public selectedBusiness: string
  public selectedProperty: string
  public selectedCompound: string
  public properties$: Subscription
  public businesses$: Subscription
  public compounds$: Subscription
  public propertyTypes$: Subscription
  public testTemplate$: Subscription
  public propertyAutocomplete$: Subscription
  public businessAutocomplete$: Subscription
  public compoundAutocomplete$: Subscription
  public onPropertyAutocomplete$ = new Subject<string>()
  public onBusinessAutocomplete$ = new Subject<string>()
  public onCompoundAutocomplete$ = new Subject<string>()
  public selectedPropertyType: string
  public isLoading = false
  public blob: Blob
  public templateFile: File | null = null

  constructor(
    private store: Store<any>,
    public translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.templates = [
      {
        title: this.translateService.instant('RDN_TEMPLATE'),
        value: 'RDN',
      },
      {
        title: this.translateService.instant('BUSINESS_TEMPLATE'),
        value: 'BUSINESS',
      },
      {
        title: this.translateService.instant('COMPOUND_TEMPLATE'),
        value: 'COMPOUND',
      },
    ]
    this.properties$ = this.store
      .pipe(select(getPropertiesState))
      .subscribe((state) => {
        const properties = state.get('data').toJS()
        this.propertiesOptions = properties.map(({ propertyCodeAssigned }) => ({
          name: propertyCodeAssigned,
          id: propertyCodeAssigned,
        }))
      })

    this.businesses$ = this.store
      .pipe(select(getBusinessesState))
      .subscribe((state) => {
        const businesses = state.get('data').toJS()
        this.propertiesOptions = businesses.map(({ code }) => ({
          name: code,
          id: code,
        }))
      })

    this.compounds$ = this.store
      .pipe(select(getCompoundsState))
      .subscribe((state) => {
        const compounds = state.get('data').toJS()
        this.compoundsOptions = compounds.map(({ code }) => ({
          name: code,
          id: code,
        }))
      })

    this.propertyTypes$ = this.store
      .pipe(select(getPropertyTypesByCodeState))
      .subscribe((state) => {
        const propertyTypes = state.toJS()
        this.propertyTypeOptions = propertyTypes.map((type: string) => ({
          name: type,
          id: type,
        }))
      })

    this.testTemplate$ = this.store
      .pipe(select(getTestTemplateState))
      .subscribe((state) => {
        this.isLoading = state.get('isLoading')
        this.blob = state.get('data')
        console.log(this.blob)
      })

    this.propertyAutocomplete$ = this.onPropertyAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchPropertiesRequest(null, {
            filters: {
              or: [{ field: 'propertyCodeAssigned', value: keyword ?? '' }],
            },
            pageSize: 100,
          }),
        )
      })

    this.businessAutocomplete$ = this.onBusinessAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchBusinessesRequest({
            filters: {
              or: [{ field: 'code', value: keyword ?? '' }],
            },
            pageSize: 100,
          }),
        )
      })

    this.compoundAutocomplete$ = this.onCompoundAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchCompoundsRequest({
            filters: {
              or: [{ field: 'code', value: keyword ?? '' }],
            },
            pageSize: 100,
          }),
        )
      })
  }

  ngOnDestroy(): void {
    this.properties$.unsubscribe()
    this.propertyTypes$.unsubscribe()
    this.propertyAutocomplete$.unsubscribe()
    this.businessAutocomplete$.unsubscribe()
    this.compoundAutocomplete$.unsubscribe()
    this.businesses$.unsubscribe()
    this.compounds$.unsubscribe()
    this.testTemplate$.unsubscribe()
  }

  public close() {
    this.onClose.emit()
  }

  public onTemplateChange(event: any) {
    this.selectedTemplate = event.value
    this.selectedBusiness = null
    this.selectedCompound = null
    this.selectedProperty = null
    this.selectedPropertyType = null

    if (event.value === 'RDN') {
      this.store.dispatch<any>(
        new FetchPropertiesRequest(null, {
          filters: {},
          pageSize: 50,
        }),
      )
    } else if (event.value === 'BUSINESS') {
      this.store.dispatch<any>(
        new FetchBusinessesRequest({
          filters: {},
          pageSize: 50,
        }),
      )
    } else if (event.value === 'COMPOUND') {
      this.store.dispatch<any>(
        new FetchCompoundsRequest({
          filters: {},
          pageSize: 50,
        }),
      )
    }
  }

  public onPropertyChange(event: any) {
    this.selectedProperty = event?.id

    if (this.selectedProperty) {
      this.store.dispatch<any>(
        new FetchPropertyTypesByCodeRequest(this.selectedProperty),
      )
    }
  }

  public get showFileField() {
    return (
      (this.selectedTemplate === 'RDN' &&
        this.selectedProperty &&
        this.selectedPropertyType) ||
      (this.selectedTemplate === 'BUSINESS' && this.selectedBusiness) ||
      (this.selectedTemplate === 'COMPOUND' && this.selectedCompound)
    )
  }

  public onSubmit() {
    this.store.dispatch<any>(
      new FetchTestTemplateRequest(this.templateFile, {
        templateType: this.selectedTemplate,
        propertyCode: this.selectedProperty,
        propertyType: this.selectedPropertyType,
        businessCode: this.selectedBusiness,
        compoundCode: this.selectedCompound,
      }),
    )
  }

  public handleFileChange(e: any) {
    this.templateFile = e.target?.files?.[0]
  }
}
